import { useEffect, useState } from 'react';
import { useConfirm } from '../components/confirm.js';
import { AreaChip } from '../components/area-chip.js';
import { appVersion } from '@mononoke/shared';
// `language`/`setLanguage` are no longer imported here: the switcher was removed in 0.1.176.
// They remain exported and used by `useLanguage`, so a stored preference still applies.
import { t } from '../platform/i18n.js';
import { useLanguage } from '../platform/use-language.js';
import { setThemePreference, subscribeTheme, themePreference, type ThemePreference } from '../platform/theme.js';
import { signOut } from '../platform/auth.js';
import { Identity } from './identity.js';
import { downloadExport, type ExportProgress } from '../platform/export.js';
import { UsersScreen } from './users.js';
import { ImportScreen } from './import.js';
import { api, call } from '../platform/api.js';
import { ledger } from '../platform/browser.js';
import type { FeedRow } from '../../../api/src/catalog/feeds.js';

/** The API origin, for building the subscription URL a calendar app will fetch. It is
 * a build-time VITE_ var like every other (adding one needs a redeploy). */
const API_URL = (import.meta.env.VITE_API_URL as string | undefined)?.replace(/\/+$/, '') ?? '';

/**
 * The settings corner (maintainer's placement ruling, 27 Jul) — and the first place
 * the Guardrail 3 export has ever been CLICKABLE.
 *
 * That gap was found by the 27 Jul evening review, by grepping the web app for the
 * export route and finding nothing: the promise "you can always get your data out"
 * had been kept for CI and for `curl` since 0.1.14, and not for the person it was
 * made to. The route needed no change — a link cannot carry a bearer token, which is
 * why a button had to wait for `platform/export.ts`.
 *
 * The full export is deliberately the LOUD control here: it is the guarantee. The
 * selection export lives on the items screen instead, where a selection exists.
 */
type SettingsPanel = 'general' | 'users' | 'import' | 'export';

/**
 * Which panels a role sees — the Settings half of the 10 Aug visibility ruling. Users,
 * Import and Export are `structure`/`full_export` work: every control in them refuses a
 * non-admin, so showing the tabs was offering three doorways into rooms of refusals.
 * HIDING, not gating — the procedures behind them refuse regardless (the standing rule).
 * `null` (whoami not landed) shows only General: for the beat before the answer, a missing
 * tab is a flicker; a briefly-visible admin tab on a student's phone is a question.
 */
function panelsFor(role: string | null): SettingsPanel[] {
  return role === 'admin' ? ['general', 'users', 'import', 'export'] : ['general'];
}

export function SettingsScreen({
  panel: initial,
  role = null,
}: { panel?: SettingsPanel; role?: string | null } = {}): JSX.Element {
  /**
   * ⚠️ USERS AND IMPORT ARE PANELS HERE, NOT TABS OF THEIR OWN (maintainer, 5 Aug).
   *
   * They were two of eight destinations in the More menu, and neither is a place the atelier
   * WORKS — they are things you set up. Folding them in leaves the nav for the screens that
   * answer a question about garments.
   *
   * Their routes still resolve: `/users` and `/import` open Settings on the right panel, so
   * every existing link and bookmark lands where it used to. The screens are rendered
   * WHOLE — `UsersScreen` and `ImportScreen` unchanged — rather than copied in, because a
   * second copy of the import flow is the last thing this codebase needs.
   */
  const [wanted, setPanel] = useState<SettingsPanel>(initial ?? 'general');
  /**
   * DERIVED, NOT CLAMPED IN STATE: a non-admin deep-linking `/users` wants the users panel and
   * gets General; if whoami then answers `admin`, the SAME state re-derives to the panel they
   * asked for. Clamping in state would throw the intent away in the beat before the role lands.
   */
  const panel: SettingsPanel = panelsFor(role).includes(wanted) ? wanted : 'general';
  // Re-render this screen when the language changes, so the switcher's own labels
  // change with it rather than after the next navigation.
  useLanguage();
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);
  const [feeds, setFeeds] = useState<FeedRow[] | null>(null);
  const [confirmEl, ask] = useConfirm();
  const [theme, setTheme] = useState<ThemePreference>(themePreference());
  useEffect(() => subscribeTheme(setTheme), []);

  /**
   * DIAGNOSTICS (restored 19 Aug) — the copyable support payload from the error ledger, including
   * the UPDATE_TIMING note. The header button that opened this went with the app-name band at
   * 0.1.170; the payload itself (`ledger.toSupportText()`) never left. `report` holds the text so a
   * blocked clipboard still leaves it on screen to select by hand (the `.selectable` opt-out of the
   * app-wide `user-select: none`).
   */
  const [diagnostics, setDiagnostics] = useState<string | null>(null);
  const [diagnosticsCopied, setDiagnosticsCopied] = useState(false);
  const copyDiagnostics = async (): Promise<void> => {
    const text = ledger.toSupportText();
    setDiagnostics(text);
    try {
      await navigator.clipboard.writeText(text);
      setDiagnosticsCopied(true);
      window.setTimeout(() => setDiagnosticsCopied(false), 2500);
    } catch {
      // Clipboard blocked (no permission / insecure context): the report is on screen to copy by
      // hand — a failure that hides the very thing it was asked for is exactly what G10 forbids.
      setDiagnosticsCopied(false);
    }
  };

  useEffect(() => {
    void (async () => {
      const result = await call('listCalendarFeeds', () => api.listCalendarFeeds.query());
      if (result.ok) setFeeds(result.data);
    })();
  }, []);

  const addFeed = async (): Promise<void> => {
    if (busy) return;
    setBusy(true);
    setFailed(false);
    const result = await call('createCalendarFeed', () => api.createCalendarFeed.mutate({ label: undefined }));
    if (result.ok) {
      setFeeds(result.data.feeds);
      setNote('Calendar link created — copy it into Google, Apple or Outlook.');
    } else {
      setNote(result.failure.message);
      setFailed(true);
    }
    setBusy(false);
  };

  const revoke = async (feedId: string): Promise<void> => {
    // Destructive-ish and irreversible for anyone already subscribed, so it names
    // what happens (standing rule for destructive scope).
    if (busy) return;
    if (
      !(await ask({
        title: 'Revoke this calendar link?',
        body: 'Anyone subscribed to it stops receiving updates. This cannot be undone from the app.',
        confirmLabel: 'Revoke link',
      }))
    )
      return;
    setBusy(true);
    setFailed(false);
    const result = await call('revokeCalendarFeed', () => api.revokeCalendarFeed.mutate({ feedId }));
    if (result.ok) {
      setFeeds(result.data.feeds);
      setNote(null);
    } else {
      setNote(result.failure.message);
      setFailed(true);
    }
    setBusy(false);
  };

  /**
   * ⚠️ THE PROGRESS IS ITS OWN STATE, NOT FOLDED INTO `note`. `note` is the OUTCOME — the
   * sentence that stays on screen after the download and says what was saved. Overwriting it
   * four times a second while the export runs would destroy the previous result before the
   * new one existed, so a failed retry would clear the record of the failure it was retrying.
   */
  const [progress, setProgress] = useState<ExportProgress | null>(null);

  const runExport = async (): Promise<void> => {
    setBusy(true);
    setNote(null);
    setFailed(false);
    setProgress({ phase: 'preparing', elapsedMs: 0 });
    const outcome = await downloadExport(undefined, setProgress);
    setProgress(null);
    setNote(outcome.message);
    setFailed(!outcome.ok);
    setBusy(false);
  };

  return (
    <section>
      {confirmEl}
      {/* THE 56px HEADER BAND (redesign 7a): the title and the tabs share the band, the same
          `.screen-head` shell every other screen wears — settings had them floating on the
          surface with the tabs stacked above a bare heading. The tablist semantics are
          unchanged (a screen reader still announces a position, not four anonymous buttons). */}
      <div className="screen-head board-head">
        <AreaChip area="settings" />
        <div className="detail-tabs" role="tablist" aria-label="Settings panels">
          {(
            [
              ['general', 'General'],
              ['users', 'Users'],
              ['import', t('nav.import')],
              ['export', t('settings.export')],
            ] as const
          )
            .filter(([key]) => panelsFor(role).includes(key))
            .map(([key, label]) => (
              <button
                key={key}
                type="button"
                role="tab"
                id={`settings-tab-${key}`}
                aria-selected={panel === key}
                aria-controls={`settings-panel-${key}`}
                className={panel === key ? 'detail-tab detail-tab-on' : 'detail-tab'}
                onClick={() => setPanel(key)}
              >
                {label}
              </button>
            ))}
        </div>
      </div>


      {panel === 'users' && (
        <div role="tabpanel" id="settings-panel-users" aria-labelledby="settings-tab-users">
          <UsersScreen />
        </div>
      )}
      {panel === 'import' && (
        <div role="tabpanel" id="settings-panel-import" aria-labelledby="settings-tab-import">
          <ImportScreen />
        </div>
      )}

      {/* THE STATUS LINE BELONGS TO THE SCREEN, NOT TO A PANEL (0.1.172).
          `note` is written by the export AND by the calendar link, which now live on different
          tabs — while it rendered inside the export panel, creating a calendar link from
          General set a message nobody could see. A write that reports nothing is the silent
          failure G10 exists to stop, and it was caught by `calendar-feed.spec`. */}
      {/*
        ⚠️ TWO SHAPES, BECAUSE ONLY ONE OF THE TWO PHASES HAS A REAL PERCENTAGE.

        The server builds the whole CSV before sending a byte (`export-route.ts` calls
        `reply.send(result.csv)`), so during `preparing` there is genuinely nothing to measure
        — and a bar creeping across on a timer would be inventing a number. What IS true is
        how long it has been going, which is also the thing that tells somebody it has not
        hung. An indeterminate bar plus a rising count of seconds says exactly that and no
        more.

        `downloading` gets a real `<progress>` with a real max, because by then the bytes are
        countable. Where `content-length` is absent it falls back to the count alone rather
        than to a made-up denominator.
      */}
      {progress !== null && (
        <p role="status" aria-live="polite">
          {progress.phase === 'preparing' && (
            <>
              <progress className="export-progress" />{' '}
              Building your catalogue export… {Math.round(progress.elapsedMs / 1000)}s
              {progress.elapsedMs > 20_000 && (
                <>
                  <br />
                  <small>
                    A full catalogue takes a while — it is assembled in one piece before the
                    download starts. Leave this tab open.
                  </small>
                </>
              )}
            </>
          )}
          {progress.phase === 'downloading' && (
            <>
              {progress.totalBytes === null ? (
                <>
                  <progress className="export-progress" /> Downloading…{' '}
                  {Math.round(progress.receivedBytes / 1024)} kB so far
                </>
              ) : (
                <>
                  <progress
                    className="export-progress"
                    value={progress.receivedBytes}
                    max={progress.totalBytes}
                  />{' '}
                  Downloading… {Math.round((progress.receivedBytes / progress.totalBytes) * 100)}%
                </>
              )}
            </>
          )}
          {progress.phase === 'saving' && <>Saving the file…</>}
        </p>
      )}
      {note !== null && <p role={failed ? 'alert' : 'status'}>{note}</p>}

      <div
        role="tabpanel"
        id="settings-panel-export"
        aria-labelledby="settings-tab-export"
        hidden={panel !== 'export'}
        className="settings-panel"
      >
      {/* 2n: the section head is the quiet uppercase label the frames use across Settings, not
          a card title. */}
      <h3 className="settings-section-label">Export the catalogue</h3>
      {/* The explainer is muted prose; THE PROMISE ALONE gets the quiet green band
          (redesign 12a) — it is the founding grievance answered, and folding the file
          description into the guarantee diluted both. Copy is the client's, verbatim. */}
      <p className="settings-lede">
        The complete catalogue as one CSV file: every item, every custom field, photo keys and tag codes.
      </p>
      <p className="quiet-band">
        It is yours, always — no plan, no limit, no permission beyond being an administrator of this atelier.
      </p>
      {/* The costly misunderstanding, answered BEFORE the download (12a): a photo key in
          a CSV cell looks like a broken image link to anyone who has not been told. */}
      <p className="info-band">
        <small>Photo keys are filenames, not the photographs. The pictures stay in storage; the CSV names them.</small>
      </p>
      {/* ⚠️ FRAME 2n DRAWS TWO FACT ROWS HERE — "Rows · N garments" and "Last export · never" —
          and NEITHER can ship honestly (Guardrail 9, no invented data). The row count needs an
          export-summary read the app deliberately lacks (the same gap that keeps a count off the
          button below); "last export" needs a stored timestamp nothing records. Left OUT rather
          than invented; both are logged in the redesign deferrals for when a summary read exists. */}
      {/* THE GUARANTEE IS THE ONE FLOATING PRIMARY (frame 2n, rule 4): Guardrail 3, made the
          prominent act — a pill bottom-right on a phone, in place on a desk, like every other
          screen's primary. The COUNT the frame names on it stays flagged for the same reason. */}
      <div className="board-primary-act">
        <button type="button" className="btn-primary" disabled={busy} onClick={() => void runExport()}>
          {busy ? 'Preparing…' : t('settings.exportFull')}
        </button>
      </div>

      </div>

      <div
        role="tabpanel"
        id="settings-panel-general"
        aria-labelledby="settings-tab-general"
        hidden={panel !== 'general'}
        className="settings-panel"
      >
      {/* 2j: SECTIONS IN THE QUIET TREATMENT, NOT CARDS — an uppercase section label over its
          rows, the same shape Users/Import/Export wear, replacing the run of raised cards. And
          APPEARANCE LEADS: the theme is the setting a person actually reaches for; the calendar
          link is the one control here with a consequence, so it keeps its warn band. */}

      {/* APPEARANCE — Light / Dark / System as the SEGMENTED CONTROL (7a): three visible states
          beat a dropdown hiding two. The select below survives only as the storage semantics. */}
      <h3 className="settings-section-label">{t('settings.theme')}</h3>
      <p className="segmented" role="group" aria-label={t('settings.theme')}>
        {(['light', 'dark', 'system'] as const).map((value) => (
          <button
            key={value}
            type="button"
            className={theme === value ? 'segment segment-on' : 'segment'}
            aria-pressed={theme === value}
            onClick={() => setThemePreference(value)}
          >
            {t(`theme.${value}`)}
          </button>
        ))}
      </p>

      {/* CALENDAR */}
      <h3 className="settings-section-label">{t('settings.calendar')}</h3>
      <p>Subscribe to the atelier’s bookings in Google, Apple or Outlook.</p>
      {/* THE KEY WARNING IS AN AMBER BAND ATTACHED TO THE CONTROL IT IS ABOUT (7a), copy
          verbatim — a warning three paragraphs from its button is a warning read after the
          click. */}
      <p className="warn-band">
        The link below is a key: anyone who has it can read <strong>every booking title, its dates and its
        client’s name</strong> — so treat it like a password, and revoke it if it goes somewhere it should not.
      </p>
      {feeds !== null && feeds.filter((f) => f.revokedAt === null).length === 0 && (
        <p>
          <button type="button" disabled={busy} onClick={() => void addFeed()}>
            Create a calendar link
          </button>
        </p>
      )}
      {(feeds ?? [])
        .filter((f) => f.revokedAt === null)
        .map((feed) => (
          <p key={feed.id}>
            <input
              type="text"
              readOnly
              className="feed-url"
              aria-label="Calendar subscription link"
              value={`${API_URL}/feeds/bookings/${feed.token}.ics`}
              onFocus={(e) => e.currentTarget.select()}
            />{' '}
            <button type="button" disabled={busy} onClick={() => void revoke(feed.id)}>
              Revoke
            </button>
            <br />
            <small>
              {feed.lastFetchedAt === null
                ? 'Not fetched yet — paste it into a calendar app to subscribe.'
                : `Last fetched ${new Date(feed.lastFetchedAt).toLocaleString()}`}
            </small>
          </p>
        ))}

      {/* THE ATELIER'S RACKS AND THEIR PLACARDS MOVED TO THE RACK LOCATION TAB (0.1.233).
          Racks are now named through the rack picker on items, boxes and outfits — an ordinary
          inventory edit any editing role may do — so a separate admin-only editor here was the
          wrong home. Nothing about places lives in Settings any more. */}

      {/**
        * ⚠️ THE LANGUAGE SWITCHER IS REMOVED, NOT THE LANGUAGE (maintainer, 5 Aug 2026:
        * "remove the language option in settings for now").
        *
        * Dutch has been on INDEFINITE HOLD since 4 Aug and this takes away the control that
        * offered it. Everything underneath survives — `setLanguage`, both dictionaries, the
        * dictionary test and `useLanguage` — so a saved `nl` preference still renders Dutch and
        * putting the block back is a revert, not a rebuild.
        *
        * "For now" is the maintainer's word and it is why nothing was deleted.
        */}

      {/* ABOUT — label/value rows (frame 2j): version, who the database thinks you are, the
          tenant, the tagline, and the way out. `.app-version` stays on the version value —
          boot.spec and offline-shell.spec assert the running version is visible somewhere real. */}
      <h3 className="settings-section-label">{t('settings.about')}</h3>
      <div className="settings-about-row">
        <span>Version</span>
        <span className="app-version settings-about-mono">v{appVersion()}</span>
      </div>
      {/* Signed in · Tenant — the whoami proof of the auth chain, as its own two rows. */}
      <Identity />
      <p className="settings-about-note">
        <small>Inventory and material flow for Haus Von Feb.</small>
      </p>

      {/* DIAGNOSTICS — the copyable support report (error ledger + notes, incl. UPDATE_TIMING).
          Restored 19 Aug: the button that opened this went with the app-name band at 0.1.170. */}
      <h3 className="settings-section-label">Diagnostics</h3>
      <div className="settings-diagnostics">
        <button type="button" className="chip" onClick={() => void copyDiagnostics()}>
          {diagnosticsCopied ? 'Copied ✓' : 'Copy diagnostics'}
        </button>
        {diagnostics !== null && (
          <button type="button" className="chip" onClick={() => setDiagnostics(null)}>
            Hide
          </button>
        )}
      </div>
      {diagnostics !== null && (
        // `.selectable` opts this out of the app-wide `user-select: none`, so the text can be
        // selected and copied by hand when the clipboard button is blocked.
        <pre className="settings-diagnostics-text selectable">{diagnostics}</pre>
      )}

      {/* THE WAY OUT — lived in the shell on every screen until 0.1.43; the account corner is
          where people look for it. Moved up out of Identity so the frame's row order holds
          (version, signed-in, tenant, tagline, then sign out). */}
      <button type="button" className="settings-signout" onClick={() => void signOut()}>
        Sign out
      </button>
      </div>
    </section>
  );
}
